import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import Router from 'next/router';
import Layout from '../../components/Layout';
import ValidationSummary from '../../components/validationSummary';
import TextInput from '../../components/textInput';
import StripeCheckout from '../../components/stripeCheckout';
import PaddleCheckout from '../../components/paddleCheckout';
const countries = require('../../countries');
const fetch = require('../../fetch');
const meetSchema = require('../../validations/meetSchema');

const plans = require('../../plans');

const scrollToRef = (ref) => {
  window.scrollTo(0, ref.current.offsetTop);
};

function NewMeet(ctx) {
  const { brand } = ctx;
  const { query } = useRouter();
  const p = query.package || 'trial';
  const defaultPlan =
    plans.find((plan) => plan.name === p) ||
    plans.find((plan) => plan.name === 'trial');
  const [countryCode, setCountryCode] = useState('GB');
  const [currency, setCurrency] = useState('GBP');
  const [meet, setMeet] = useState({
    name: '',
    displayName: '',
    numRaces: 6,
    numberOfPlayers: defaultPlan.name === 'custom' ? 50 : defaultPlan.maxSize,
    startBalance: 100,
    bettingCurrency: '£',
  });

  const [plan, setPlan] = useState(defaultPlan);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const [customPrice, setCustomPrice] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const countryUrl = '/api/country';
      const countryResponse = await fetch.fetchWithSessionCookie(
        countryUrl,
        ctx
      );
      if (countryResponse.status === 200) {
        const { countryCode } = await countryResponse.json();
        const country = countries.find((c) => c.code === countryCode);
        const c = (country && country.currency) || 'USD';
        setCurrency(c);
        setCountryCode(countryCode);
      }
    }
    fetchData();

    if (plan.name === 'custom') {
      const amount = (
        Math.round(
          plans.find((p) => p.name === 'custom').cost[currency].pricePerPlayer *
            meet.numberOfPlayers *
            100
        ) / 100
      ).toFixed(2);
      setCustomPrice(amount);
    }
  }, []);

  const validationSummary = useRef(null);
  const executeScroll = () => scrollToRef(validationSummary);

  const showStripeCheckout = () => {
    return countryCode === 'GB' || brand.name === 'atr';
  };

  function handleChange(e) {
    let value = e.target.value;
    setMeet(Object.assign({}, meet, { [e.target.name]: value }));
  }

  function handleChangeNumberOfPlayers(e) {
    let value = e.target.value;
    setMeet(Object.assign({}, meet, { [e.target.name]: value }));
    const amount = (
      Math.round(
        plans.find((p) => p.name === 'custom').cost[currency].pricePerPlayer *
          value *
          100
      ) / 100
    ).toFixed(2);
    setCustomPrice(amount);
  }

  async function handleChangePlan(e) {
    let value = e.target.value;
    const p = plans.find((p) => p.name === value);
    setPlan(p);
    const numberOfPlayers = value === 'custom' ? 50 : p.maxSize;
    if (value === 'custom') {
      const amount = (
        Math.round(
          plans.find((p) => p.name === 'custom').cost[currency].pricePerPlayer *
            numberOfPlayers *
            100
        ) / 100
      ).toFixed(2);
      setCustomPrice(amount);
    } else {
      setCustomPrice(null);
    }

    setMeet(
      Object.assign(meet, {
        numberOfPlayers,
        numRaces: 6,
        startBalance: 100,
      })
    );
  }

  function validateGame() {
    let validationResult = meetSchema.validate(meet);
    if (plan.name === 'custom') {
      if (meet.numberOfPlayers < 20) {
        validationResult.error = {
          details: [
            {
              path: 'numberOfPlayers',
              message: 'Number of players must be more than 20',
            },
          ],
        };
      }
      if (meet.numberOfPlayers > plan.maxSize) {
        validationResult.error = {
          details: [
            {
              path: 'numberOfPlayers',
              message: `Number of players must be less than ${plan.maxSize}`,
            },
          ],
        };
      }
    }

    if (validationResult.error) {
      setValidationErrors(validationResult.error.details);
      setTimeout(executeScroll, 100);
      return false;
    } else {
      setValidationErrors([]);
      return true;
    }
  }

  async function startFreeGame() {
    if (validateGame()) {
      onPaymentMethodSuccessful({});
    }
  }

  async function onPaddlePaymentMethodSuccessful({ meet, user, data, plan }) {
    const paddleOrdersUrl = '/api/paddle-orders';
    const res = await fetch.fetchWithSessionCookie(paddleOrdersUrl, ctx, {
      body: JSON.stringify({ meet, user, data, plan, domain: brand.host }),
      method: 'POST',
    });
    if (res.status === 201) {
      const { id } = await res.json();
      Router.push(`/building-meet/${id}`);
    } else {
      const errors = await res.json();
      setValidationErrors(errors);
    }
  }

  async function onPaymentMethodSuccessful({ paymentIntent, user }) {
    if (!submitted) {
      setSubmitted(true);
      const createOrderUrl = '/api/orders';
      const res = await fetch.fetchWithSessionCookie(createOrderUrl, ctx, {
        body: JSON.stringify({
          meet,
          user,
          plan,
          paymentIntent,
          countryCode,
          domain: brand.host,
        }),
        method: 'POST',
      });
      if (res.status === 201) {
        const { id } = await res.json();
        Router.push(`/building-meet/${id}`);
      } else {
        const errors = await res.json();
        setValidationErrors(errors);
        setSubmitted(false);
      }
    }
  }
  return (
    <Layout
      brand={brand}
      showHeaderLinks={true}
      canonical="https://www.track-night.com/meets/new"
    >
      <div className="container">
        <div className="section">
          <div>
            <h1 className="title">Set up your Track Night</h1>
            <div>
              <div className="notification is-warning is-light">
                <p>
                  We&apos;ll need you to share your screen with all the other
                  players, so you&apos;re going to need to do this from{' '}
                  <strong>
                    the same computer you are using for your video chat.
                  </strong>
                </p>
              </div>
              {plan.name === 'trial' ? (
                <div className="notification is-light">
                  <p>
                    Trial games let you play a quick game with up to four
                    players to get a feel for how it all works.
                  </p>
                  <p>
                    <strong>Trial games use a fixed set of races.</strong> Paid
                    games will have different races every time!
                  </p>
                </div>
              ) : (
                <div className="notification is-light">
                  <p>
                    <strong>
                      Never played before and want to see how it all works?
                    </strong>
                  </p>
                  <p>
                    Select &quot;Trial Game&quot; in the menu below to try it
                    out for free.
                  </p>
                </div>
              )}
              <div ref={validationSummary}>
                <ValidationSummary validationErrors={validationErrors} />
              </div>
              <div className="columns">
                <div className="column">
                  <div className="field">
                    <label htmlFor="plan" className="label">
                      Game size
                    </label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select value={plan.name} onChange={handleChangePlan}>
                          {plans.map((p) => {
                            return (
                              <option key={`plan-${p.name}`} value={p.name}>
                                {p.sizeText}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="column is-4">
                  <div className="field has-text-centered">
                    <label className="label">Price</label>
                    <div className="control">
                      <div className="price has-text-centered">
                        {plan.cost[currency].priceText}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="field">
                  <label htmlFor="name" className="label">
                    Give your night a name
                  </label>
                  <div className="control">
                    <TextInput
                      id="name"
                      validationErrors={validationErrors}
                      value={meet.name}
                      onChange={handleChange}
                      name="name"
                      placeholder="Birthday Race Night"
                    />
                  </div>
                </div>
                <div className="columns">
                  <div className="column">
                    <div className="field">
                      <label htmlFor="displayName" className="label">
                        What&apos;s your name?
                      </label>
                      <div className="control ">
                        <TextInput
                          id="displayName"
                          validationErrors={validationErrors}
                          value={meet.displayName}
                          onChange={handleChange}
                          name="displayName"
                          placeholder="Hungry Paul"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="column">
                    <div className="field">
                      <label htmlFor="bettingCurrency" className="label">
                        Betting currency
                      </label>
                      <div className="select is-fullwidth">
                        <select
                          name="bettingCurrency"
                          value={plan.bettingCurrency}
                          onChange={handleChange}
                        >
                          <option key={`plan-currency-1}`} value={'£'}>
                            &pound;
                          </option>
                          <option key={`plan-currency-2}`} value={'$'}>
                            $
                          </option>
                          <option key={`plan-currency-3}`} value={'€'}>
                            €
                          </option>
                          <option key={`plan-currency-3}`} value={'☘️'}>
                            ☘️
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {plan.name === 'custom' ? (
                  <div className="columns">
                    <div className="column">
                      <div className="field">
                        <label htmlFor="plan" className="label">
                          Number of players
                        </label>
                        <div className="control">
                          <TextInput
                            type="number"
                            validationErrors={validationErrors}
                            value={meet.numberOfPlayers}
                            onChange={handleChangeNumberOfPlayers}
                            name="numberOfPlayers"
                            placeholder="100"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label htmlFor="plan" className="label">
                          Start Balance
                        </label>
                        <div className="control">
                          <TextInput
                            type="number"
                            validationErrors={validationErrors}
                            value={meet.startBalance}
                            onChange={handleChange}
                            name="startBalance"
                            placeholder="50"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label htmlFor="plan" className="label">
                          Number of Races
                        </label>
                        <div className="control">
                          <TextInput
                            type="number"
                            validationErrors={validationErrors}
                            value={meet.numRaces}
                            onChange={handleChange}
                            name="numRaces"
                            placeholder="6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="num-races">
                  <p>
                    <strong>
                      Each game has 6 races and usually lasts around 40 minutes.
                    </strong>
                  </p>
                </div>
                {plan.cost[currency].price == 0 ? (
                  <div>
                    <div className="field">
                      <button
                        type="button"
                        className="button is-success is-fullwidth is-large"
                        onClick={startFreeGame}
                      >
                        Start
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="notification is-info is-light">
                    {showStripeCheckout() ? (
                      <StripeCheckout
                        brand={brand}
                        customPrice={customPrice}
                        plan={plan}
                        setCountryCode={setCountryCode}
                        setCurrency={setCurrency}
                        countryCode={countryCode}
                        defaultCurrency={currency}
                        onPaymentMethodSuccessful={onPaymentMethodSuccessful}
                        validateGame={validateGame}
                      />
                    ) : (
                      <PaddleCheckout
                        brand={brand}
                        numPlayers={meet.numberOfPlayers}
                        productId={plan.paddleId}
                        meet={meet}
                        plan={plan}
                        setCountryCode={setCountryCode}
                        countryCode={countryCode}
                        setCurrency={setCurrency}
                        defaultCurrency={currency}
                        onPaymentMethodSuccessful={
                          onPaddlePaymentMethodSuccessful
                        }
                        validateGame={validateGame}
                      />
                    )}
                  </div>
                )}
                <div className="has-text-centered">
                  <br />
                  <p>
                    By using the site you agree to our{' '}
                    <a href="/terms">terms and conditions</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .num-races {
          text-align: center;
          margin: 2em 0;
        }
        .paymentForm {
          margin-bottom: 1em;
          max-width: 600px;
          margin: auto;
        }
        .top-padding-when-wide {
          margin-top: 15px;
        }
        @media (max-width: 768px) {
          .top-padding-when-wide {
            margin-top: 0px;
          }
        }
        .price {
          font-weight: bold;
          font-size: 1.3em;
        }
      `}</style>
    </Layout>
  );
}

export default NewMeet;
